import {
  Badge,
  Button,
  Card,
  CardContent,
  CommandSelectComponent,
  cn,
  Kanban,
  KanbanBoard,
  KanbanColumn,
  KanbanColumnContent,
  KanbanColumnHeader,
  KanbanColumnTitle,
  KanbanItem,
  KanbanOverlay,
  ToggleGroup,
  ToggleGroupItem,
  Typography,
} from "@atlas/core";
import type { IApplicationGroup, IIdpGroup, IUiGroupMapping } from "@models";
import { ArrowDown, ArrowRight, Link2, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import {
  getGroupKanbanItemId,
  type IGroupKanbanItem,
  KANBAN_COLUMN_APP,
  KANBAN_COLUMN_IDP,
  KANBAN_COLUMN_MAPPING,
  MAPPING_FIGMA_COLORS,
  MAPPING_ZONE_KANBAN_ITEM,
  type MappingFilterTab,
  toKanbanItem,
} from "./applicationGroupMappingUtils";
import type { useApplicationGroupMapping } from "./useApplicationGroupMapping";

const KANBAN_COLUMN_CHROME = "w-full self-stretch";

const COLUMN_SHELL =
  "box-border flex w-full flex-col overflow-hidden rounded-[10px] border border-border bg-card";

const COLUMN_HEADER =
  "flex-col items-start gap-1 shrink-0 rounded-t-[10px] border-0 border-b border-border bg-card px-4 py-3";

const COLUMN_TOOLBAR = "shrink-0 space-y-2 border-b border-border px-3 py-3";

const COLUMN_LIST = "flex flex-col gap-2 px-3 pb-3 pt-2";

const COLUMN_BODY = "flex flex-col gap-3 px-3 pb-4 pt-2";

function MappingColumnHint({ children }: { children: ReactNode }) {
  return (
    <Typography.Muted
      className="mt-0 mb-0 text-xs"
      style={{ color: MAPPING_FIGMA_COLORS.hintText }}>
      {children}
    </Typography.Muted>
  );
}

function MappingFilterToggle({
  value,
  onChange,
  t,
}: {
  value: MappingFilterTab;
  onChange: (tab: MappingFilterTab) => void;
  t: (key: string) => string;
}) {
  return (
    <ToggleGroup
      type="single"
      groupVariant="withSpacing"
      value={value}
      onValueChange={(next: string) => {
        if (next) onChange(next as MappingFilterTab);
      }}
      size="sm"
      variant="outline"
      className="ml-auto flex-wrap justify-end">
      <ToggleGroupItem value="all" className="shrink-0">
        {t("hub.administer.filter_all")}
      </ToggleGroupItem>
      <ToggleGroupItem value="mapped" className="shrink-0">
        {t("hub.administer.filter_mapped")}
      </ToggleGroupItem>
      <ToggleGroupItem value="unmapped" className="shrink-0">
        {t("hub.administer.filter_unmapped")}
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

function ColumnSearch({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
}) {
  return (
    <CommandSelectComponent
      variant="sm"
      displayType="search"
      shouldFilter={false}
      debounceMs={300}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className="w-full"
    />
  );
}

function SideGroupCard({
  item,
  isSelected,
  isMapped,
  onSelect,
  mappedLabel,
}: {
  item: IGroupKanbanItem;
  isSelected: boolean;
  isMapped: boolean;
  onSelect: () => void;
  mappedLabel: string;
}) {
  return (
    <KanbanItem
      value={getGroupKanbanItemId(item)}
      showDragIcon
      showCardStyle
      className={cn(isSelected && "ring-2 ring-[#1E417A] border-[#1E417A]/35")}
      onClick={onSelect}>
      <>
        <header className="flex items-start justify-between gap-2">
          <Typography.Small className="text-[13px] font-semibold leading-snug text-foreground">
            {item.name}
          </Typography.Small>
          {isMapped ? (
            <Badge
              size="sm"
              appearance="outline"
              className={cn(
                "shrink-0",
                item.kind === "app"
                  ? "bg-[#1E417A]/8 text-[#1E417A] border-[#1E417A]/35"
                  : "bg-[#087443]/8 text-[#087443] border-[#087443]/35",
              )}>
              {mappedLabel}
            </Badge>
          ) : null}
        </header>
        {item.description ? (
          <Typography.Muted
            className="mt-0 mb-0 line-clamp-2 text-xs"
            style={{ color: MAPPING_FIGMA_COLORS.hintText }}>
            {item.description}
          </Typography.Muted>
        ) : null}
      </>
    </KanbanItem>
  );
}

function StagingGroupSlot({
  group,
  placeholder,
  accentColor,
  onClear,
  clearAriaLabel,
}: {
  group: { name: string } | undefined;
  placeholder: string;
  accentColor: string;
  onClear?: () => void;
  clearAriaLabel?: string;
}) {
  if (group) {
    return (
      <Card className="w-full shadow-none">
        <CardContent className="flex items-center gap-1 px-2 py-2">
          <Typography.Small
            className="min-w-0 flex-1 truncate text-center text-[13px] font-semibold"
            style={{ color: accentColor }}>
            {group.name}
          </Typography.Small>
          {onClear ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 shrink-0"
              onClick={onClear}
              aria-label={clearAriaLabel}>
              <Trash2 className="size-4" />
            </Button>
          ) : null}
        </CardContent>
      </Card>
    );
  }
  return (
    <Typography.Muted className="mt-0 mb-0 text-sm italic">
      {placeholder}
    </Typography.Muted>
  );
}

export type ApplicationGroupMappingBoardModel = ReturnType<
  typeof useApplicationGroupMapping
>;

interface IApplicationGroupMappingBoardProps {
  model: ApplicationGroupMappingBoardModel;
}

export function ApplicationGroupMappingBoard({
  model,
}: IApplicationGroupMappingBoardProps) {
  const {
    t,
    selectedAppMapId,
    kanbanValue,
    handleKanbanValueChange,
    handleKanbanMove,
    filteredAppGroups,
    filteredIdpGroups,
    filteredMappings,
    mappings,
    mappedAppGroupIds,
    mappedIdpGroupIds,
    selectedAppGroupId,
    selectedIdpGroupId,
    selectedAppGroup,
    selectedIdpGroup,
    appSearch,
    setAppSearch,
    idpSearch,
    setIdpSearch,
    mappingSearch,
    setMappingSearch,
    appFilter,
    setAppFilter,
    idpFilter,
    setIdpFilter,
    handleCreateMapping,
    handleRemoveMapping,
    toggleAppGroupSelection,
    toggleIdpGroupSelection,
  } = model;

  const mappedLabel = t("hub.administer.filter_mapped");

  return (
    <Kanban<IGroupKanbanItem>
      key={selectedAppMapId}
      value={kanbanValue}
      onValueChange={handleKanbanValueChange}
      onMove={handleKanbanMove}
      getItemValue={getGroupKanbanItemId}
      className="flex flex-col">
      <KanbanBoard className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-3">
        <KanbanColumn
          value={KANBAN_COLUMN_APP}
          className={KANBAN_COLUMN_CHROME}>
          <Card className={COLUMN_SHELL}>
            <KanbanColumnHeader className={COLUMN_HEADER}>
              <KanbanColumnTitle
                className="text-[15px] font-bold"
                style={{ color: MAPPING_FIGMA_COLORS.appBlue }}>
                {t("hub.administer.application_groups_column")}
              </KanbanColumnTitle>
              <MappingColumnHint>
                {t("hub.administer.application_groups_column_hint")}
              </MappingColumnHint>
            </KanbanColumnHeader>
            <CardContent className={COLUMN_TOOLBAR}>
              <ColumnSearch
                value={appSearch}
                onChange={setAppSearch}
                placeholder={t("hub.administer.search_application_groups")}
              />
              <MappingFilterToggle
                value={appFilter}
                onChange={setAppFilter}
                t={t}
              />
            </CardContent>
            <KanbanColumnContent
              value={KANBAN_COLUMN_APP}
              className={COLUMN_LIST}>
              {filteredAppGroups.length === 0 ? (
                <Typography.Muted className="mt-0 mb-0 py-6 text-center text-sm">
                  {t("hub.administer.no_application_groups")}
                </Typography.Muted>
              ) : (
                filteredAppGroups.map((group: IApplicationGroup) => (
                  <SideGroupCard
                    key={group.id}
                    item={toKanbanItem(group, "app")}
                    isSelected={selectedAppGroupId === group.id}
                    isMapped={mappedAppGroupIds.has(group.id)}
                    onSelect={() => toggleAppGroupSelection(group.id)}
                    mappedLabel={mappedLabel}
                  />
                ))
              )}
            </KanbanColumnContent>
          </Card>
        </KanbanColumn>

        <KanbanColumn
          value={KANBAN_COLUMN_MAPPING}
          className={KANBAN_COLUMN_CHROME}>
          <Card className={COLUMN_SHELL}>
            <KanbanColumnHeader className={COLUMN_HEADER}>
              <KanbanColumnTitle
                className="text-[15px] font-bold"
                style={{ color: MAPPING_FIGMA_COLORS.mappingGray }}>
                {t("hub.administer.mapping_column")}
              </KanbanColumnTitle>
              <MappingColumnHint>
                {t("hub.administer.mapping_column_hint")}
              </MappingColumnHint>
            </KanbanColumnHeader>

            <CardContent className={COLUMN_TOOLBAR}>
              <ColumnSearch
                value={mappingSearch}
                onChange={setMappingSearch}
                placeholder={t("hub.administer.search_active_mapping")}
              />
            </CardContent>

            <CardContent className={COLUMN_BODY}>
              <Card className="shrink-0 border-2 border-dashed bg-muted/20 shadow-none">
                <CardContent className="p-4">
                  <Typography.Small className="mb-3 block font-semibold text-muted-foreground">
                    {t("hub.administer.drag_drop_zone")}
                  </Typography.Small>
                  <KanbanColumnContent
                    value={KANBAN_COLUMN_MAPPING}
                    className="min-h-20 gap-2 !p-0">
                    <KanbanItem
                      value={getGroupKanbanItemId(MAPPING_ZONE_KANBAN_ITEM)}
                      draggable={false}
                      showDragIcon={false}
                      showCardStyle={false}
                      className="w-full cursor-default border-0 bg-transparent p-0 shadow-none hover:border-transparent focus-visible:ring-0">
                      <section className="flex w-full flex-col items-center gap-2 py-2 text-center">
                        <StagingGroupSlot
                          group={selectedAppGroup}
                          placeholder={t("hub.administer.select_app_group")}
                          accentColor={MAPPING_FIGMA_COLORS.appBlue}
                          onClear={
                            selectedAppGroup
                              ? () =>
                                  toggleAppGroupSelection(selectedAppGroup.id)
                              : undefined
                          }
                          clearAriaLabel={t(
                            "hub.administer.remove_selected_app_group",
                          )}
                        />
                        <ArrowDown className="size-5 text-muted-foreground" />
                        <StagingGroupSlot
                          group={selectedIdpGroup}
                          placeholder={t("hub.administer.select_idp_group")}
                          accentColor={MAPPING_FIGMA_COLORS.idpGreen}
                          onClear={
                            selectedIdpGroup
                              ? () =>
                                  toggleIdpGroupSelection(selectedIdpGroup.id)
                              : undefined
                          }
                          clearAriaLabel={t(
                            "hub.administer.remove_selected_idp_group",
                          )}
                        />
                      </section>
                    </KanbanItem>
                  </KanbanColumnContent>

                  <footer className="mt-4 flex justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCreateMapping}
                      disabled={!selectedAppGroup || !selectedIdpGroup}>
                      <Link2 className="size-4" />
                      {t("hub.administer.create_mapping")}
                    </Button>
                  </footer>
                </CardContent>
              </Card>

              <section className="shrink-0">
                <Typography.Small className="mb-2 block font-semibold text-foreground">
                  {t("hub.administer.active_mappings")} ({mappings.length})
                </Typography.Small>
                <section className="space-y-2">
                  {mappings.length === 0 ? (
                    <Typography.Muted className="mt-0 mb-0 py-4 text-center text-xs">
                      {t("hub.administer.no_mappings_yet")}
                    </Typography.Muted>
                  ) : filteredMappings.length === 0 ? (
                    <Typography.Muted className="mt-0 mb-0 py-4 text-center text-xs">
                      {t("hub.administer.no_mappings_match_search", {
                        defaultValue: "No mappings match your search.",
                      })}
                    </Typography.Muted>
                  ) : (
                    filteredMappings.map((mapping: IUiGroupMapping) => (
                      <Card
                        key={mapping.id}
                        className="bg-muted/30 shadow-none">
                        <CardContent className="flex items-center gap-2 p-2">
                          <section className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                            <Typography.Small
                              className="truncate text-[13px] font-semibold"
                              style={{ color: MAPPING_FIGMA_COLORS.appBlue }}>
                              {mapping.applicationGroupName}
                            </Typography.Small>
                            <ArrowRight
                              className="size-4 shrink-0"
                              style={{
                                color: MAPPING_FIGMA_COLORS.mappingGray,
                              }}
                            />
                            <Typography.Small
                              className="truncate text-[13px] font-semibold"
                              style={{
                                color: MAPPING_FIGMA_COLORS.idpGreen,
                              }}>
                              {mapping.idpGroupName}
                            </Typography.Small>
                          </section>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveMapping(mapping.id)}
                            aria-label={t("hub.administer.remove_mapping")}>
                            <Trash2 className="size-4" />
                          </Button>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </section>
              </section>
            </CardContent>
          </Card>
        </KanbanColumn>

        <KanbanColumn
          value={KANBAN_COLUMN_IDP}
          className={KANBAN_COLUMN_CHROME}>
          <Card className={COLUMN_SHELL}>
            <KanbanColumnHeader className={COLUMN_HEADER}>
              <KanbanColumnTitle
                className="text-[15px] font-bold"
                style={{ color: MAPPING_FIGMA_COLORS.idpGreen }}>
                {t("hub.administer.idp_groups_column")}
              </KanbanColumnTitle>
              <MappingColumnHint>
                {t("hub.administer.idp_groups_column_hint")}
              </MappingColumnHint>
            </KanbanColumnHeader>
            <CardContent className={COLUMN_TOOLBAR}>
              <ColumnSearch
                value={idpSearch}
                onChange={setIdpSearch}
                placeholder={t("hub.administer.search_idp_groups")}
              />
              <MappingFilterToggle
                value={idpFilter}
                onChange={setIdpFilter}
                t={t}
              />
            </CardContent>
            <KanbanColumnContent
              value={KANBAN_COLUMN_IDP}
              className={COLUMN_LIST}>
              {filteredIdpGroups.length === 0 ? (
                <Typography.Muted className="mt-0 mb-0 py-6 text-center text-sm">
                  {t("hub.administer.no_idp_groups")}
                </Typography.Muted>
              ) : (
                filteredIdpGroups.map((group: IIdpGroup) => (
                  <SideGroupCard
                    key={group.id}
                    item={toKanbanItem(group, "idp")}
                    isSelected={selectedIdpGroupId === group.id}
                    isMapped={mappedIdpGroupIds.has(group.id)}
                    onSelect={() => toggleIdpGroupSelection(group.id)}
                    mappedLabel={mappedLabel}
                  />
                ))
              )}
            </KanbanColumnContent>
          </Card>
        </KanbanColumn>
      </KanbanBoard>
      <KanbanOverlay />
    </Kanban>
  );
}
